import { Event } from "../../../../base/common/events";
import { createDecorator } from "../../../../platform/instantiation/common/registry";



export const IComponentsService = createDecorator<IComponentsService>('IComponentsService');

export interface IComponentsService {

    readonly onDidChange: Event<void>;
    readonly onDidAddComponent: Event<DidAddComponentEvent>;

    registerHandler(handler: IComponentsHandler): void;

    /** announce that the available component TYPES changed (project code
     *  registered new ones) — pickers re-derive their lists on this */
    notifyChanged(): void;

    /**
     * Get component options for a given context
     */
    getComponentOptions(context?: GetComponentOptionsContext): GetComponentOptionsResult;

    /**
     * Add a component to the target object
     */
    addComponent(componentId: string): Promise<AddComponentResult>;
    /**
     * Add a component to the object behind a HIERARCHY NODE (drag-drop on a tree row)
     */
    addComponentToNode(componentId: string, nodeId: string): Promise<AddComponentResult>;
    /**
     * Add a component to the object under a SCREEN POSITION (drag-drop on the viewport)
     */
    addComponentAtPosition(componentId: string, position: { x: number; y: number }): Promise<AddComponentResult>;
    /**
     * Remove a component from the target object
     */
    removeComponent(componentId: string): Promise<RemoveComponentResult>;

    /** MRU ids of recently ADDED components (Add-menu "Recent" section) */
    getRecentComponents?(): string[];

    /**
     * Wrap a picked value for a TYPED reference field (e.g. an "AssetReference"
     * field that accepts a scene object OR a url) into the runtime value the
     * component expects. This is the seam that keeps engine types out of the UI:
     * core.ui must never import the engine (the chrome extension bundles the UI
     * and MUST NOT contain the engine) — the product's handler constructs the
     * real value. Returns undefined to clear the field.
     */
    createReferenceValue?(fieldType: string, value: unknown): unknown;
}

export interface IComponentsHandler {
    getComponentOptions(context: GetComponentOptionsContext): Pick<GetComponentOptionsResult, "options">;
    addComponent(componentId: string): Promise<AddComponentResult>;
    addComponentToNode?(componentId: string, nodeId: string): Promise<AddComponentResult>;
    addComponentAtPosition?(componentId: string, position: { x: number; y: number }): Promise<AddComponentResult>;
    removeComponent(componentId: string): Promise<RemoveComponentResult>;
    /** MRU ids of recently ADDED components */
    getRecentComponents?(): string[];
    /** See {@link IComponentsService.createReferenceValue} */
    createReferenceValue?(fieldType: string, value: unknown): unknown;
}


export interface GetComponentOptionsContext {
    /** The target object to get component options for */
    // readonly targets: object[];
    sort?: "name";
}

export interface GetComponentOptionsResult {
    readonly options: ComponentOption[];
}
export interface ComponentOption {
    readonly id: string;
    /** The raw class name — the thing to search / match / refer to it by. */
    readonly name: string;
    /**
     * Human-readable form of {@link name} for display ("BasicIKConstraint" →
     * "Basic IK Constraint"). Filled in by the service when a handler doesn't
     * provide one, so UIs never derive it themselves. Presentational only: keep
     * searching against `name` as well, or typing the class name stops matching.
     */
    readonly displayName?: string;
    readonly description?: string;
    readonly tooltip?: string;
    readonly icon?: string;
    /** grouping for add-component UIs (e.g. "Physics", "Animation") — absent = ungrouped */
    readonly category?: string;
    /** ALL categories/tags of the type (the grouping `category` is the first) —
     *  search matches these after the name, so "physics" surfaces the category */
    readonly tags?: readonly string[];
}

export interface AddComponentResult {
    readonly success: boolean;
    readonly message?: string;
}

export interface RemoveComponentResult {
    readonly success: boolean;
    readonly message?: string;
}

export interface DidAddComponentEvent {
    readonly componentId: string;
}